import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search, RefreshCw, CheckCircle2, XCircle, Mail, MailWarning,
  Clock, Building2, AlertTriangle, Send,
} from "lucide-react";
import { apiUtils } from "../../../utils/apiUtils";
import SimpleModal from "../../composite/SimpleModal";

/**
 * PilotProgramManagement - review and decide pilot applications.
 *
 * Schedules-first layout mirroring the Monitoring page: a filterable, sortable
 * table where the row is the unit of work, clicking a row opens detail, and the
 * per-row actions stay on the row. Approving creates the account and emails a
 * one-time set-password link; declining emails a courteous notice.
 */

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-200" },
  contacted: { label: "Contacted", cls: "bg-brand-50 text-brand-700 ring-brand-600/20 dark:bg-brand-500/10 dark:text-brand-200" },
  accepted: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-200" },
  declined: { label: "Declined", cls: "bg-muted text-muted-foreground ring-border" },
};

const SORTS = {
  newest: { label: "Newest first", fn: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) },
  oldest: { label: "Oldest first", fn: (a, b) => new Date(a.createdAt) - new Date(b.createdAt) },
  name: { label: "Name (A-Z)", fn: (a, b) => (a.name || "").localeCompare(b.name || "") },
  company: { label: "Company (A-Z)", fn: (a, b) => (a.companyName || "").localeCompare(b.companyName || "") },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

/** Delivery state of the decision email - an approval nobody received is not done. */
function EmailState({ entry }) {
  const le = entry.lastEmail;
  if (!le || le.sent === null || le.sent === undefined) return null;
  if (le.sent) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-700" title={`Sent ${formatDate(le.at)}`}>
        <Mail className="h-3.5 w-3.5" /> Emailed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-red-600" title={le.error || "Send failed"}>
      <MailWarning className="h-3.5 w-3.5" /> Email failed
    </span>
  );
}

export default function PilotProgramManagement() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [sortKey, setSortKey] = useState("newest");
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null); // { entry, action }
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listRes, statsRes] = await Promise.all([
        apiUtils.get("/api/waitlist?limit=100"),
        apiUtils.get("/api/waitlist/stats").catch(() => null),
      ]);
      const list = listRes?.data?.data || [];
      setEntries(Array.isArray(list) ? list : []);
      if (statsRes?.data?.data) setStats(statsRes.data.data);
    } catch (err) {
      setError(
        err.response?.status === 403
          ? "You need global admin access to review pilot applications."
          : "Could not load pilot applications."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries
      .filter((e) => (statusFilter === "all" ? true : e.status === statusFilter))
      .filter((e) =>
        !q ||
        [e.name, e.email, e.companyName].some((v) => (v || "").toLowerCase().includes(q))
      )
      .sort(SORTS[sortKey].fn);
  }, [entries, search, statusFilter, sortKey]);

  const act = async (entry, action) => {
    setBusyId(entry._id);
    setConfirm(null);
    try {
      const res = await apiUtils.post(`/api/waitlist/${entry._id}/${action}`);
      const body = res?.data || {};
      setToast({
        type: body.emailSent === false ? "warn" : "success",
        message: body.message || "Done.",
      });
      await load();
      setDetail(null);
    } catch (err) {
      setToast({
        type: "error",
        message: err.response?.data?.message || `Could not ${action} this application.`,
      });
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const tiles = stats && [
    { label: "Pending review", value: stats.pending, tone: "text-amber-600" },
    { label: "Approved", value: stats.accepted, tone: "text-emerald-600" },
    { label: "Declined", value: stats.declined, tone: "text-muted-foreground" },
    { label: "Last 7 days", value: stats.recentSignups, tone: "text-brand-600" },
  ];

  return (
    <div className="space-y-5">
      {toast && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            toast.type === "error"
              ? "border-red-200 bg-red-50 text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200"
              : toast.type === "warn"
              ? "border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200"
              : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200"
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}

      {/* Stat tiles */}
      {tiles && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-xl border border-border bg-card p-4 shadow-card">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t.label}</p>
              <p className={`mt-1 text-2xl font-semibold ${t.tone}`}>{t.value ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or company…"
            className="w-full rounded-lg border border-input py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-input px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        >
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="accepted">Approved</option>
          <option value="declined">Declined</option>
          <option value="all">All statuses</option>
        </select>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="rounded-lg border border-input px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        >
          {Object.entries(SORTS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button
          onClick={load}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      {/* Empty / loading */}
      {loading && !entries.length ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
          Loading applications…
        </div>
      ) : !filtered.length ? (
        <div className="rounded-xl border border-dashed border-input bg-card p-10 text-center">
          <Clock className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">
            {entries.length ? "No applications match these filters" : "No applications yet"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {entries.length
              ? "Try a different status or clear the search."
              : "Applications submitted from the landing page appear here."}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-border bg-card shadow-card md:block">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted">
                <tr>
                  {["Applicant", "Company", "Applied", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((entry) => (
                  <tr
                    key={entry._id}
                    onClick={() => setDetail(entry)}
                    className="cursor-pointer transition-colors hover:bg-muted"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{entry.name}</div>
                      <div className="text-xs text-muted-foreground">{entry.email}</div>
                    </td>
                    <td className="px-4 py-3 text-foreground">{entry.companyName || <span className="text-muted-foreground">Individual</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(entry.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <StatusPill status={entry.status} />
                        <EmailState entry={entry} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <RowActions entry={entry} busy={busyId === entry._id} onAct={(a) => setConfirm({ entry, action: a })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filtered.map((entry) => (
              <div
                key={entry._id}
                onClick={() => setDetail(entry)}
                className="rounded-xl border border-border bg-card p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{entry.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{entry.email}</p>
                  </div>
                  <StatusPill status={entry.status} />
                </div>
                <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {entry.companyName || "Individual"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">Applied {formatDate(entry.createdAt)}</p>
                <div className="mt-3 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                  <EmailState entry={entry} />
                  <RowActions entry={entry} busy={busyId === entry._id} onAct={(a) => setConfirm({ entry, action: a })} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Detail */}
      <SimpleModal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? detail.name : ""}
        size="xl"
      >
        {detail && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" value={detail.email} />
              <Field label="Company" value={detail.companyName || "Individual"} />
              <Field label="Applied" value={formatDate(detail.createdAt)} />
              <Field label="Status" value={<StatusPill status={detail.status} />} />
              {detail.reviewedAt && <Field label="Reviewed" value={formatDate(detail.reviewedAt)} />}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                What they want to use RepCheck for
              </p>
              <p className="mt-1.5 whitespace-pre-wrap rounded-lg bg-muted p-3 text-sm text-foreground">
                {detail.useCase}
              </p>
            </div>

            {detail.lastEmail?.sent === false && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
                <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  The last email to this applicant failed: {detail.lastEmail.error}. They have
                  not been told. Use Resend once email is working.
                </span>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
              <RowActions
                entry={detail}
                busy={busyId === detail._id}
                expanded
                onAct={(a) => setConfirm({ entry: detail, action: a })}
              />
            </div>
          </div>
        )}
      </SimpleModal>

      {/* Confirm - approve and decline both send real email to a real person */}
      <SimpleModal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={
          confirm?.action === "approve"
            ? "Approve this application?"
            : confirm?.action === "reject"
            ? "Decline this application?"
            : "Resend email?"
        }
      >
        {confirm && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {confirm.action === "approve" && (
                <>
                  This creates a RepCheck account for <strong>{confirm.entry.email}</strong> and
                  emails them a link to set their password. The link is valid for 72 hours.
                </>
              )}
              {confirm.action === "reject" && (
                <>
                  This emails <strong>{confirm.entry.email}</strong> to let them know they
                  haven't been offered a place in this round. No reason is given.
                </>
              )}
              {confirm.action === "resend" && (
                <>This sends the most recent decision email to <strong>{confirm.entry.email}</strong> again.</>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => act(confirm.entry, confirm.action)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                  confirm.action === "reject" ? "bg-destructive hover:bg-destructive/90" : "bg-brand-600 hover:bg-brand-700"
                }`}
              >
                {confirm.action === "approve" ? "Approve and send invite"
                  : confirm.action === "reject" ? "Decline and notify"
                  : "Resend"}
              </button>
            </div>
          </div>
        )}
      </SimpleModal>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm text-foreground">{value}</div>
    </div>
  );
}

function RowActions({ entry, busy, onAct, expanded }) {
  const decided = entry.status === "accepted" || entry.status === "declined";
  const base = "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50";

  return (
    <div className={`flex items-center gap-2 ${expanded ? "" : "justify-end"}`}>
      {!decided && (
        <>
          <button
            disabled={busy}
            onClick={() => onAct("approve")}
            className={`${base} bg-brand-600 text-white hover:bg-brand-700`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
          </button>
          <button
            disabled={busy}
            onClick={() => onAct("reject")}
            className={`${base} border border-input text-foreground hover:bg-muted`}
          >
            <XCircle className="h-3.5 w-3.5" /> Decline
          </button>
        </>
      )}
      {decided && (
        <button
          disabled={busy}
          onClick={() => onAct("resend")}
          className={`${base} border border-input text-foreground hover:bg-muted`}
        >
          <Send className="h-3.5 w-3.5" /> Resend email
        </button>
      )}
    </div>
  );
}
